import {
    AlertTriangle,
    CheckCircle,
    Clock,
    HelpCircle,
    Loader2,
    type LucideIcon,
    ShieldCheck,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import { DocumentStatus } from './types';

interface DocumentStatusBadgeProps {
    status: DocumentStatus;
    size?: 'sm' | 'md' | 'lg';
    mode?: 'small' | 'large';
    className?: string;
}

interface StatusConfig {
    icon: LucideIcon;
    label: string;
    description: string;
    color: string;
    textColor: string;
    bgColor: string;
    pulsate?: boolean;
    animate?: boolean;
}

export function DocumentStatusBadge({
    status,
    size = 'md',
    mode = 'small',
    className,
}: DocumentStatusBadgeProps) {
    const statusConfig: Record<DocumentStatus, StatusConfig> = {
        [DocumentStatus.pending]: {
            icon: Clock,
            label: 'Pending',
            description: 'Document is waiting to be processed',
            color: 'bg-gray-500',
            textColor: 'text-gray-400',
            bgColor: 'bg-gray-100',
        },
        [DocumentStatus.processing_ocr]: {
            icon: Loader2,
            label: 'Processing',
            description: 'Document is being scanned for text',
            color: 'bg-blue-500',
            textColor: 'text-blue-700',
            bgColor: 'bg-blue-100',
            animate: true,
        },
        [DocumentStatus.processing_compliance]: {
            icon: Loader2,
            label: 'Processing',
            description: 'Document is being checked for compliance',
            color: 'bg-blue-500',
            textColor: 'text-blue-700',
            bgColor: 'bg-blue-100',
            animate: true,
        },
        [DocumentStatus.ai_verified]: {
            icon: ShieldCheck,
            label: 'AI Checked',
            description: 'Document has been verified by AI',
            color: 'bg-purple-500',
            textColor: 'text-purple-700',
            bgColor: 'bg-purple-100',
        },
        [DocumentStatus.requires_attention]: {
            icon: AlertTriangle,
            label: 'Missing',
            description: 'Document needs human review',
            color: 'bg-red-500',
            textColor: 'text-red-700',
            bgColor: 'bg-red-100',
            pulsate: true,
        },
        [DocumentStatus.verified]: {
            icon: CheckCircle,
            label: 'Ready',
            description: 'Document has been fully verified',
            color: 'bg-green-600',
            textColor: 'text-green-700',
            bgColor: 'bg-green-100',
        },
        [DocumentStatus.ground_truth]: {
            icon: CheckCircle,
            label: 'Ground Truth',
            description: 'Document has been annotated and verified as ground truth',
            color: 'bg-teal-600',
            textColor: 'text-teal-700',
            bgColor: 'bg-teal-100',
        },
    };

    const fallback: StatusConfig = {
        icon: HelpCircle,
        label: 'Unknown',
        description: 'Status not recognised',
        color: 'bg-gray-400',
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-100',
    };

    const config = statusConfig[status] ?? fallback;
    const Icon = config.icon;

    const circleSizes = {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
    };

    const iconSizes = {
        sm: 'h-4 w-4',
        md: 'h-3 w-3',
        lg: 'h-4 w-4',
    };

    const isPulsating = config.pulsate;
    const isAnimating = config.animate;

    // Small mode (icon only)
    if (mode === 'small') {
        // For small size, just show the icon without background
        if (size === 'sm') {
            return (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                className={cn(
                                    'inline-flex items-center justify-center',
                                    {
                                        'animate-pulse': isPulsating,
                                    },
                                    className,
                                )}
                            >
                                <Icon
                                    className={cn(
                                        iconSizes[size],
                                        config.textColor,
                                        {
                                            'animate-spin': isAnimating,
                                        },
                                    )}
                                />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                <span className="font-medium">
                                    {config.label}
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {config.description}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            );
        }

        // For md and lg sizes, keep the original circular background
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className={cn(
                                'relative rounded-full inline-flex items-center justify-center',
                                circleSizes[size],
                                config.color,
                                {
                                    'animate-pulse': isPulsating,
                                    'shadow-[0_0_10px_rgba(168,85,247,0.7)]':
                                        isPulsating,
                                },
                                className,
                            )}
                        >
                            <Icon
                                className={cn(iconSizes[size], 'text-white', {
                                    'animate-spin': isAnimating,
                                })}
                            />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="font-medium">{config.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {config.description}
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    // Large mode (icon with text)
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
                            config.bgColor,
                            config.textColor,
                            {
                                'animate-pulse': isPulsating,
                                'shadow-[0_0_10px_rgba(239,68,68,0.3)]':
                                    isPulsating,
                            },
                            className,
                        )}
                    >
                        <Icon
                            className={cn('h-4 w-4', {
                                'animate-spin': isAnimating,
                            })}
                        />
                        <span>{config.label}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs">{config.description}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
